import { gql, useMutation } from '@apollo/client';
import { Box, Typography, Card, Stack, Link as JoyLink } from '@mui/joy';
import { useNavigate } from 'react-router';
import { useFeedback } from '../feedback/Service';
import { parseGraphQLError } from '../../utils';
import useUser from './useUser';
import { getAllProviders } from '../integrations/providers';

const SELECT_ATS_PROVIDER = gql`
  mutation SelectATSProvider($providerId: String!) {
    selectATSProvider(providerId: $providerId) {
      _id
      usesATS
      selectedATSProvider
    }
  }
`;

const ATSSelector = () => {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const { user } = useUser();
  const providers = getAllProviders();

  const [selectProvider, { loading }] = useMutation(SELECT_ATS_PROVIDER, {
    update(cache, { data: { selectATSProvider } }) {
      cache.modify({
        id: cache.identify({ _id: user._id, __typename: 'User' }),
        fields: {
          client: (existing) => ({
            ...existing,
            usesATS: selectATSProvider.usesATS,
            selectedATSProvider: selectATSProvider.selectedATSProvider,
          }),
        },
      });
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    },
  });

  const handleSelect = async (providerId) => {
    await selectProvider({ variables: { providerId } });
    navigate('/onboarding/connect-ats');
  };

  const handleSkip = async () => {
    if (loading) return;
    await selectProvider({ variables: { providerId: 'skip' } });
    navigate('/jobs');
  };

  return (
    <Box
      sx={{
        my: 'auto',
        py: 2,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: 'sm',
        mx: 'auto',
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography level="h4" gutterBottom>Which ATS do you use?</Typography>
        <Typography level="body-sm" color="neutral">
          Select your applicant tracking system to get started
        </Typography>
      </Box>

      <Stack spacing={2}>
        {providers.map((provider) => (
          <Card
            key={provider.id}
            variant="outlined"
            onClick={() => !loading && handleSelect(provider.id)}
            sx={{
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              '&:hover': { borderColor: 'primary.400', boxShadow: 'sm' },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography level="title-md">{provider.name}</Typography>
                <Typography level="body-sm">{provider.description}</Typography>
                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                  {provider.connectionType === 'apiKey' ? 'Connects via API key' : 'Connects via OAuth'}
                </Typography>
              </Box>
            </Stack>
          </Card>
        ))}
      </Stack>

      <Typography level="body-xs" color="neutral" sx={{ textAlign: 'center', mt: 1 }}>
        Don&apos;t see your ATS? Let us know &mdash; we&apos;re adding new integrations regularly.
      </Typography>

      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <JoyLink
          level="body-sm"
          color="neutral"
          component="button"
          onClick={handleSkip}
        >
          Skip for now
        </JoyLink>
      </Box>
    </Box>
  );
};

export default ATSSelector;
